import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, FileText, ShoppingBag, Shield, ArrowUpRight, Loader2, Sparkles, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { db, collection, getDocs, query, where } from '@/firebase';

interface SearchResultItem {
  id: string;
  title: string;
  description: string;
  type: 'service' | 'product' | 'article';
  link: string;
  category?: string;
  price?: number;
}

interface GlobalSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const staticServices = [
  { id: 'sbu', title: 'Pembuatan SBU', description: 'Sertifikat Badan Usaha untuk legalitas perusahaan konstruksi Anda.', type: 'service' as const, link: '/services/sbu', category: 'Legalitas' },
  { id: 'skk', title: 'Sertifikat SKK', description: 'Sertifikat Kompetensi Kerja bagi tenaga ahli konstruksi.', type: 'service' as const, link: '/services/skk', category: 'Tenaga Ahli' },
  { id: 'nib-oss', title: 'NIB & OSS', description: 'Pengurusan Nomor Induk Berusaha dan perizinan sistem OSS RBA.', type: 'service' as const, link: '/services/nib-oss', category: 'Perizinan Dasar' },
  { id: 'konsultasi-perizinan', title: 'Konsultasi Perizinan', description: 'Pendampingan lengkap untuk perizinan proyek, PBG, SLF, dan AMDAL.', type: 'service' as const, link: '/services/konsultasi-perizinan', category: 'Izin Teknis' },
  { id: 'pendampingan-proyek', title: 'Pendampingan Proyek', description: 'Bantuan administrasi dan teknis lapangan selama jalannya proyek.', type: 'service' as const, link: '/services/pendampingan-proyek', category: 'Manajemen' },
  { id: 'penyusunan-dokumen-penawaran', title: 'Penyusunan Dokumen Penawaran', description: 'Jasa penyusunan dokumen penawaran teknis dan administrasi tender.', type: 'service' as const, link: '/services/penyusunan-dokumen-penawaran', category: 'Tender' },
  { id: 'pajak-badan-pribadi', title: 'Pajak Badan & Orang Pribadi', description: 'Pelaporan Pajak Badan Usaha dan Pajak Orang Pribadi secara terpadu.', type: 'service' as const, link: '/services/pajak-badan-pribadi', category: 'Perpajakan' },
  { id: 'spt-masa-efaktur', title: 'SPT Masa & E-Faktur', description: 'Pelaporan SPT Masa PPN, SPT Masa PPh, serta pembuatan E-Faktur.', type: 'service' as const, link: '/services/spt-masa-efaktur', category: 'Perpajakan Bullanan' },
  { id: 'edukasi-pelatihan', title: 'Edukasi & Pelatihan', description: 'Materi belajar terstruktur mengenai standar teknis dan regulasi konstruksi.', type: 'service' as const, link: '/education', category: 'Edukasi' }
];

export default function GlobalSearchModal({ isOpen, onClose }: GlobalSearchModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Esc key closure
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      setTimeout(() => inputRef.current?.focus(), 100);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Click outside closure
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  // Live query logic across all datasets
  useEffect(() => {
    if (!searchTerm.trim()) {
      setResults([]);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setLoading(true);
      const queryLower = searchTerm.toLowerCase();

      try {
        const tempResults: SearchResultItem[] = [];

        // 1. Filter local static services
        const matchedServices = staticServices.filter(s => 
          s.title.toLowerCase().includes(queryLower) || 
          s.description.toLowerCase().includes(queryLower)
        );
        tempResults.push(...matchedServices);

        // 2. Fetch products from Firestore
        const productsSnap = await getDocs(collection(db, 'products'));
        productsSnap.docs.forEach((doc) => {
          const data = doc.data();
          const name = data.name || '';
          const desc = data.description || '';
          if (name.toLowerCase().includes(queryLower) || desc.toLowerCase().includes(queryLower)) {
            tempResults.push({
              id: doc.id,
              title: name,
              description: desc.substring(0, 100) + (desc.length > 100 ? '...' : ''),
              type: 'product',
              link: `/product/${doc.id}`,
              price: data.price,
              category: 'Dokumen & Aplikasi'
            });
          }
        });

        // 3. Fetch articles from Firestore
        const articlesQuery = query(collection(db, 'articles'), where('status', '==', 'published'));
        const articlesSnap = await getDocs(articlesQuery);
        articlesSnap.docs.forEach((doc) => {
          const data = doc.data();
          const title = data.title || '';
          const excerpt = data.excerpt || '';
          if (title.toLowerCase().includes(queryLower) || excerpt.toLowerCase().includes(queryLower)) {
            tempResults.push({
              id: doc.id,
              title: title,
              description: excerpt || data.content?.substring(0, 100) || '',
              type: 'article',
              link: `/article/${data.slug}`,
              category: data.category || 'Wawasan'
            });
          }
        });

        setResults(tempResults);
      } catch (err) {
        console.error("Gagal melakukan pencarian global:", err);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const clearSearch = () => {
    setSearchTerm('');
    setResults([]);
    inputRef.current?.focus();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 z-50 flex items-start justify-center pt-24 md:pt-32 px-4 bg-background/80 backdrop-blur-md"
        onClick={handleBackdropClick}
      >
        <motion.div 
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          ref={modalRef}
          className="w-full max-w-2xl bg-card border border-border/80 shadow-2xl rounded-3xl overflow-hidden flex flex-col max-h-[75vh]"
          id="global-search-container"
        >
          {/* Header Input bar */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-border/50 bg-background/50">
            <Search className="h-5 w-5 text-foreground/40 shrink-0" />
            <input 
              ref={inputRef}
              type="text" 
              placeholder="Cari layanan, template dokumen, regulasi, atau artikel..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-grow bg-transparent text-sm text-foreground outline-none border-none py-2 placeholder-foreground/30 font-sans"
              id="global-search-input"
            />
            {searchTerm && (
              <button 
                type="button"
                onClick={clearSearch} 
                className="p-1 rounded-full text-foreground/45 hover:text-foreground hover:bg-muted/50 transition-all shrink-0"
                id="search-clear-btn"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border border-border/50 bg-muted px-1.5 font-mono text-[10px] font-medium text-foreground/40 shrink-0">
              ESC
            </kbd>
          </div>

          {/* Results Lists */}
          <div className="flex-1 overflow-y-auto px-6 py-4 font-sans max-h-[50vh] divide-y divide-border/30">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-foreground/50">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm">Mencari di seluruh platform...</span>
              </div>
            ) : searchTerm && results.length === 0 ? (
              <div className="text-center py-12 text-foreground/45 flex flex-col items-center">
                <div className="h-10 w-10 bg-muted rounded-full flex items-center justify-center mb-3">
                  <Search className="h-5 w-5 opacity-40" />
                </div>
                <p className="text-sm font-semibold mb-1 text-foreground">Tidak ada hasil ditemukan</p>
                <p className="text-xs">Coba kata kunci lain atau klasifikasi administrasi yang berbeda.</p>
              </div>
            ) : !searchTerm ? (
              <div className="py-6 space-y-4">
                <div className="flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-wider mb-2">
                  <Sparkles className="h-4 w-4 text-gold fill-current" />
                  <span>Pencarian Terpopuler</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-foreground/70">
                  <button onClick={() => setSearchTerm('SBU')} className="text-left px-4 py-2.5 bg-muted/30 border border-border/40 hover:border-gold/30 rounded-xl hover:bg-gold/5 transition-all flex items-center justify-between text-xs font-bold leading-none">
                    <span>Pembuatan SBU Konstruksi</span>
                    <ArrowUpRight className="h-3 w-3 text-gold" />
                  </button>
                  <button onClick={() => setSearchTerm('Template')} className="text-left px-4 py-2.5 bg-muted/30 border border-border/40 hover:border-gold/30 rounded-xl hover:bg-gold/5 transition-all flex items-center justify-between text-xs font-bold leading-none">
                    <span>Template Excel Dokumen Tender</span>
                    <ArrowUpRight className="h-3 w-3 text-gold" />
                  </button>
                  <button onClick={() => setSearchTerm('SKK')} className="text-left px-4 py-2.5 bg-muted/30 border border-border/40 hover:border-gold/30 rounded-xl hover:bg-gold/5 transition-all flex items-center justify-between text-xs font-bold leading-none">
                    <span>Sertifikat Kompetensi Tenaga Ahli</span>
                    <ArrowUpRight className="h-3 w-3 text-gold" />
                  </button>
                  <button onClick={() => setSearchTerm('Pajak')} className="text-left px-4 py-2.5 bg-muted/30 border border-border/40 hover:border-gold/30 rounded-xl hover:bg-gold/5 transition-all flex items-center justify-between text-xs font-bold leading-none">
                    <span>Layanan Laporan Perpajakan Badan</span>
                    <ArrowUpRight className="h-3 w-3 text-gold" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4 py-2">
                {/* Result Groups */}
                {['service', 'product', 'article'].map((groupType) => {
                  const filtered = results.filter(item => item.type === groupType);
                  if (filtered.length === 0) return null;

                  return (
                    <div key={groupType} className="py-3">
                      <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-foreground/45 mb-2.5 flex items-center gap-1.5">
                        {groupType === 'service' ? (
                          <><Shield className="h-3.5 w-3.5 text-gold" /> Jasa & Layanan</>
                        ) : groupType === 'product' ? (
                          <><ShoppingBag className="h-3.5 w-3.5 text-emerald-500" /> Dokumen & Aplikasi</>
                        ) : (
                          <><BookOpen className="h-3.5 w-3.5 text-blue-500" /> Edukasi & Artikel</>
                        )}
                        <span className="text-[9px] bg-muted px-1.5 py-0.5 rounded text-foreground/40 font-normal">
                          {filtered.length}
                        </span>
                      </h4>

                      <div className="space-y-1">
                        {filtered.map((item) => (
                          <Link 
                            key={item.id}
                            to={item.link}
                            onClick={onClose}
                            className="flex items-center gap-3 p-3 hover:bg-muted/40 border border-transparent hover:border-border/60 rounded-2xl transition-all group/item"
                            id={`search-item-${item.id}`}
                          >
                            <div className="flex-grow min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="font-bold text-sm text-foreground group-hover/item:text-primary transition-colors line-clamp-1">{item.title}</span>
                                {item.category && (
                                  <span className="px-2 py-0.5 text-[9px] font-bold bg-muted text-foreground/60 rounded-full select-none shrink-0 border border-border/20">
                                    {item.category}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-foreground/50 line-clamp-1">{item.description}</p>
                            </div>
                            {item.price !== undefined && (
                              <span className="text-xs font-black text-primary shrink-0">
                                Rp {item.price.toLocaleString('id-ID')}
                              </span>
                            )}
                            <div className="h-7 w-7 rounded-full bg-muted/20 group-hover/item:bg-primary/10 flex items-center justify-center shrink-0 transition-colors">
                              <ArrowUpRight className="h-3.5 w-3.5 text-foreground/40 group-hover/item:text-primary transition-colors" />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
